/**
 * Data Connectors Management
 * Handles database connection UI and management
 */

(function () {
  'use strict';

  // Database configuration metadata
  const DATABASE_CONFIG = {
    postgresql: {
      name: 'PostgreSQL',
      defaultPort: 5432,
      connectionFormat: 'postgresql://user:password@host:port/dbname',
      hasSchema: true
    },
    mysql: {
      name: 'MySQL',
      defaultPort: 3306,
      connectionFormat: 'mysql://user:password@host:port/dbname',
      hasSchema: false
    },
    bigquery: {
      name: 'Google BigQuery',
      defaultPort: null,
      connectionFormat: 'bigquery://project_id/dataset_id',
      hasSchema: false,
      requiresCredentials: true
    },
    redshift: {
      name: 'Amazon Redshift',
      defaultPort: 5439,
      connectionFormat: 'redshift://user:password@host:port/dbname',
      hasSchema: true
    },
    snowflake: {
      name: 'Snowflake',
      defaultPort: null,
      connectionFormat: 'snowflake://user:password@account/database/schema?warehouse=warehouse',
      hasSchema: true,
      requiresWarehouse: true
    },
    mongodb: {
      name: 'MongoDB',
      defaultPort: 27017,
      connectionFormat: 'mongodb://user:password@host:port/dbname?authSource=admin',
      hasSchema: false
    },
    elasticsearch: {
      name: 'Elasticsearch',
      defaultPort: 9200,
      connectionFormat: 'http://host:port',
      hasSchema: false,
      requiresApiKey: true
    }
  };

  let selectedDatabaseType = null;
  let savedConnections = [];
  let dataConnectorsInitialized = false;
  let connectorTriggerHandlersBound = false;
  let initAttempts = 0;
  const MAX_INIT_ATTEMPTS = 5;
  const FALLBACK_BACKDROP_ID = 'data-connector-dialog-backdrop';

  function ensureDialogSupport(dialog) {
    if (!dialog) return null;

    if (dialog.dataset.connectorDialogMode) {
      return dialog.dataset.connectorDialogMode;
    }

    if (typeof dialog.showModal === 'function') {
      dialog.dataset.connectorDialogMode = 'native';
    } else if (typeof dialogPolyfill !== 'undefined' && dialogPolyfill?.registerDialog) {
      dialogPolyfill.registerDialog(dialog);
      dialog.dataset.connectorDialogMode = 'polyfill';
    } else {
      applyFallbackDialogStyling(dialog);
      dialog.dataset.connectorDialogMode = 'fallback';
      console.warn('Data connector dialog using fallback modal behaviour (showModal not available)');
    }

    return dialog.dataset.connectorDialogMode;
  }

  function applyFallbackDialogStyling(dialog) {
    if (!dialog) return;

    dialog.setAttribute('role', dialog.getAttribute('role') || 'dialog');
    dialog.style.position = dialog.style.position || 'fixed';
    dialog.style.top = dialog.style.top || '50%';
    dialog.style.left = dialog.style.left || '50%';
    dialog.style.transform = dialog.style.transform || 'translate(-50%, -50%)';
    dialog.style.zIndex = dialog.style.zIndex || '1001';
    dialog.style.backgroundColor = dialog.style.backgroundColor || '#fff';
    dialog.style.borderRadius = dialog.style.borderRadius || '4px';
    dialog.style.boxShadow = dialog.style.boxShadow || '0 12px 28px rgba(0, 0, 0, 0.24)';
    dialog.style.minWidth = dialog.style.minWidth || '320px';
  }

  function getFallbackBackdrop() {
    let backdrop = document.getElementById(FALLBACK_BACKDROP_ID);

    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.id = FALLBACK_BACKDROP_ID;
      backdrop.style.position = 'fixed';
      backdrop.style.top = '0';
      backdrop.style.left = '0';
      backdrop.style.width = '100%';
      backdrop.style.height = '100%';
      backdrop.style.background = 'rgba(0, 0, 0, 0.45)';
      backdrop.style.zIndex = '1000';
      backdrop.style.display = 'none';
      backdrop.addEventListener('click', function () {
        const connectorDialog = document.querySelector('#data-connector-dialog');
        closeDialog(connectorDialog);
      });
      document.body.appendChild(backdrop);
    }

    return backdrop;
  }

  function showDialog(dialog) {
    const mode = ensureDialogSupport(dialog);
    if (!mode) return false;

    if ((mode === 'native' || mode === 'polyfill') && typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else if (typeof dialog.show === 'function') {
      dialog.show();
    } else {
      dialog.setAttribute('open', 'true');
      dialog.style.display = 'block';
      const backdrop = getFallbackBackdrop();
      if (backdrop) {
        backdrop.style.display = 'block';
      }
    }

    return true;
  }

  function closeDialog(dialog) {
    if (!dialog) return;

    const mode = ensureDialogSupport(dialog);

    if ((mode === 'native' || mode === 'polyfill') && typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      dialog.style.display = 'none';
      const backdrop = document.getElementById(FALLBACK_BACKDROP_ID);
      if (backdrop) {
        backdrop.style.display = 'none';
      }
    }
  }

  function onDocumentReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function handleReady() {
        document.removeEventListener('DOMContentLoaded', handleReady);
        callback();
      });
    } else {
      // Run on next tick so DOM mutations injected by templates settle
      setTimeout(callback, 0);
    }
  }

  function getTextFieldContainer(input) {
    if (!input || typeof input.closest !== 'function') {
      return null;
    }
    return input.closest('.mdl-textfield');
  }

  function syncTextFieldState(input) {
    const field = getTextFieldContainer(input);
    if (!field) {
      return;
    }

    const hasValue = String(input.value || '').trim().length > 0;
    field.classList.toggle('is-dirty', hasValue);
  }

  function setTextFieldValue(selector, value) {
    const input = document.querySelector(selector);
    if (!input) {
      return;
    }

    input.value = value ?? '';
    syncTextFieldState(input);
  }

  function syncConnectionFormTextFields() {
    document.querySelectorAll('#connection-form .mdl-textfield input').forEach(syncTextFieldState);
  }

  function hasDataConnectorSurface() {
    return Boolean(document.querySelector('#data-connector-dialog, #add-connector-btn'));
  }

  onDocumentReady(bootstrapDataConnectors);

  function bootstrapDataConnectors() {
    if (dataConnectorsInitialized) {
      return;
    }

    const initialized = initializeDataConnectors();
    if (!initialized && initAttempts < MAX_INIT_ATTEMPTS) {
      initAttempts += 1;
      setTimeout(bootstrapDataConnectors, 150 * initAttempts);
    } else if (!initialized && initAttempts >= MAX_INIT_ATTEMPTS && hasDataConnectorSurface()) {
      console.warn('Data connector dialog still unavailable after retries');
    }
  }

  function initializeDataConnectors() {
    if (dataConnectorsInitialized) {
      return true;
    }

    const dialog = document.querySelector('#data-connector-dialog');
    if (!dialog) {
      return false;
    }
    ensureDialogSupport(dialog);
    bindConnectorTriggerHandlers();

    // Add connector button click handler
    const addConnectorBtn = document.querySelector('#add-connector-btn');
    if (addConnectorBtn) {
      addConnectorBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openConnectorDialog();
      });
    }

    // Database type selection
    document.querySelectorAll('.db-type-option').forEach(option => {
      option.addEventListener('click', function () {
        selectDatabaseType(this.dataset.dbType);
      });
    });

    // Connection string toggle
    const useConnString = document.querySelector('#use-conn-string');
    if (useConnString) {
      useConnString.addEventListener('change', function () {
        toggleConnectionStringMode(this.checked);
      });
    }

    // Test connection button
    const testBtn = document.querySelector('#test-connection-btn');
    if (testBtn) {
      testBtn.addEventListener('click', testConnection);
    }

    // Save connection button
    const saveBtn = document.querySelector('#save-connection-btn');
    if (saveBtn) {
      saveBtn.addEventListener('click', saveConnection);
    }

    // Dialog close button
    const closeButton = dialog.querySelector('.close');
    if (closeButton) {
      closeButton.addEventListener('click', function () {
        closeDialog(dialog);
        resetDialog();
      });
    }

    // Native dialogs normally close themselves on Escape, but browser
    // polyfills and older engines do not consistently dispatch that behavior.
    // Handle both paths explicitly so the modal cannot become stuck open.
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeDialog(dialog);
      resetDialog();
    });
    dialog.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !dialog.open) {
        return;
      }
      event.preventDefault();
      closeDialog(dialog);
      resetDialog();
    });

    // Load existing connections
    loadConnections();

    dataConnectorsInitialized = true;
    return true;
  }

  function bindConnectorTriggerHandlers() {
    if (connectorTriggerHandlersBound) {
      return;
    }

    function getConnectorTrigger(event, selector) {
      const target = event && event.target;
      if (!target || typeof target.closest !== 'function') {
        return null;
      }
      return target.closest(selector);
    }

    document.addEventListener(
      'click',
      function (event) {
        const trigger = getConnectorTrigger(
          event,
          '#add-connector-btn, [data-sidebar-action="add-connector"], [data-mobile-action="add-connector"]'
        );
        if (!trigger) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        openConnectorDialog();
      },
      true
    );

    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        const trigger = getConnectorTrigger(event, '[data-sidebar-action="add-connector"]');
        if (!trigger) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        openConnectorDialog();
      },
      true
    );

    connectorTriggerHandlersBound = true;
  }

  function openConnectorDialog() {
    const dialog = document.querySelector('#data-connector-dialog');
    if (!dialog) {
      console.warn('Cannot open connector dialog before it is attached to the DOM');
      return;
    }

    // A connector dialog is modal. Dismiss the optional workbench tour first
    // so the browser does not leave a second focusable dialog above it (which
    // would consume Escape and make the connector dialog appear stuck open).
    const activeTour = window.__sqlWorkbenchTour;
    if (activeTour && typeof activeTour.cancel === 'function') {
      try {
        activeTour.cancel();
      } catch {
        // The tour is optional; a teardown race must not block connections.
      }
    }
    const opened = showDialog(dialog);
    if (opened) {
      resetDialog();
    }
  }

  function resetDialog() {
    // Reset selection
    selectedDatabaseType = null;
    document.querySelectorAll('.db-type-option').forEach(option => {
      option.style.borderColor = '#e0e0e0';
      option.style.backgroundColor = 'white';
    });

    // Hide form
    document.querySelector('#connection-form').style.display = 'none';

    // Reset form fields
    document.querySelectorAll('#connection-form input').forEach(input => {
      input.value = '';
      syncTextFieldState(input);
    });

    // Reset buttons
    document.querySelector('#test-connection-btn').style.display = 'none';
    const saveBtn = document.querySelector('#save-connection-btn');
    saveBtn.style.display = 'none';
    saveBtn.disabled = true;
    saveBtn.textContent = 'Save Connection';
    saveBtn.removeEventListener('click', updateConnection);
    saveBtn.addEventListener('click', saveConnection);

    // Reset test result
    document.querySelector('#test-connection-result').style.display = 'none';

    // Reset connection string mode
    document.querySelector('#use-conn-string').checked = false;
    toggleConnectionStringMode(false);

    // Update MDL text fields
    if (typeof componentHandler !== 'undefined') {
      componentHandler.upgradeAllRegistered();
    }
  }

  function selectDatabaseType(dbType) {
    selectedDatabaseType = dbType;
    const config = DATABASE_CONFIG[dbType];

    // Update UI selection
    document.querySelectorAll('.db-type-option').forEach(option => {
      if (option.dataset.dbType === dbType) {
        option.style.borderColor = '#4CAF50';
        option.style.backgroundColor = '#f1f8f4';
      } else {
        option.style.borderColor = '#e0e0e0';
        option.style.backgroundColor = 'white';
      }
    });

    // Show connection form
    document.querySelector('#connection-form').style.display = 'block';

    // Update connection string format hint
    document.querySelector('#conn-string-format').textContent =
      'Format: ' + config.connectionFormat;

    // Set default port
    if (config.defaultPort) {
      setTextFieldValue('#conn-port', config.defaultPort);
    } else {
      setTextFieldValue('#conn-port', '');
    }

    // Show/hide schema field
    const schemaGroup = document.querySelector('#conn-schema-group');
    if (config.hasSchema) {
      schemaGroup.style.display = 'block';
    } else {
      schemaGroup.style.display = 'none';
    }

    // Show buttons
    document.querySelector('#test-connection-btn').style.display = 'inline-block';
    document.querySelector('#save-connection-btn').style.display = 'inline-block';

    // Update MDL text fields
    if (typeof componentHandler !== 'undefined') {
      componentHandler.upgradeAllRegistered();
    }

    syncConnectionFormTextFields();
  }

  function toggleConnectionStringMode(useString) {
    const stringGroup = document.querySelector('#conn-string-group');
    const fieldsGroup = document.querySelector('#conn-fields-group');

    if (useString) {
      stringGroup.style.display = 'block';
      fieldsGroup.style.display = 'none';
    } else {
      stringGroup.style.display = 'none';
      fieldsGroup.style.display = 'block';
    }
  }

  async function testConnection() {
    const testBtn = document.querySelector('#test-connection-btn');
    const resultDiv = document.querySelector('#test-connection-result');
    const resultText = document.querySelector('#test-result-text');
    const saveBtn = document.querySelector('#save-connection-btn');

    // Disable button during test
    testBtn.disabled = true;
    testBtn.textContent = 'Testing...';

    // Reset result
    resultDiv.style.display = 'none';
    saveBtn.disabled = true;

    try {
      const connectionData = getConnectionData();

      // Add user secret
      connectionData.secret = window.userData?.secret || localStorage.getItem('secret');

      const response = await fetch('/api/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(connectionData)
      });

      const result = await response.json();

      // Show result
      resultDiv.style.display = 'block';

      if (result.success) {
        resultDiv.style.backgroundColor = '#c8e6c9';
        resultDiv.style.color = '#2e7d32';
        resultText.textContent = 'Connection successful!';
        saveBtn.disabled = false;
      } else {
        resultDiv.style.backgroundColor = '#ffcdd2';
        resultDiv.style.color = '#c62828';
        resultText.textContent = 'Connection failed: ' + (result.error || 'Unknown error');
        saveBtn.disabled = true;
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      resultDiv.style.display = 'block';
      resultDiv.style.backgroundColor = '#ffcdd2';
      resultDiv.style.color = '#c62828';
      resultText.textContent = 'Error: ' + error.message;
      saveBtn.disabled = true;
    } finally {
      testBtn.disabled = false;
      testBtn.textContent = 'Test Connection';
    }
  }

  async function saveConnection() {
    const saveBtn = document.querySelector('#save-connection-btn');

    // Disable button during save
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';

    try {
      const connectionData = getConnectionData();

      // Add user secret
      connectionData.secret = window.userData?.secret || localStorage.getItem('secret');

      const response = await fetch('/api/data-sources', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(connectionData)
      });

      const result = await response.json();

      if (result.success) {
        // Close dialog
        const dialog = document.querySelector('#data-connector-dialog');
        closeDialog(dialog);

        // Show success message
        showSnackbar('Connection saved successfully');

        // Reload connections list
        loadConnections();

        // Index tables for the new connection
        if (result.data_source?.id) {
          indexDatabaseTables(result.data_source.id);
        }
      } else {
        showSnackbar('Failed to save connection: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving connection:', error);
      showSnackbar('Error: ' + error.message);
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save Connection';
    }
  }

  function getConnectionData() {
    const useConnString = document.querySelector('#use-conn-string').checked;

    const data = {
      name: document.querySelector('#conn-name').value,
      source_type: selectedDatabaseType
    };

    if (useConnString) {
      data.connection_string = document.querySelector('#conn-string').value;
    } else {
      data.host = document.querySelector('#conn-host').value;
      data.port = document.querySelector('#conn-port').value;
      data.database = document.querySelector('#conn-database').value;
      data.username = document.querySelector('#conn-username').value;
      data.password = document.querySelector('#conn-password').value;

      const schema = document.querySelector('#conn-schema').value;
      if (schema) {
        data.db_schema = schema;
      }
    }

    return data;
  }

  async function loadConnections() {
    const treeView = document.querySelector('#connectorsTreeView');
    if (!treeView) return;

    const rawSecret = (window.userData && window.userData.secret) || localStorage.getItem('secret');
    // No authenticated session — a redirect to /login is typically in flight; skip the fetch.
    if (!rawSecret || rawSecret === 'null' || rawSecret === 'undefined') {
      return;
    }

    try {
      const response = await fetch(`/api/data-sources?secret=${encodeURIComponent(rawSecret)}`);
      if (!response.ok) {
        return;
      }
      const result = await response.json();

      if (result.success && result.data_sources) {
        savedConnections = result.data_sources;
        renderConnectionsList();
      }
    } catch (error) {
      // Failed-to-fetch during navigation (e.g. redirect to /login) shows up as TypeError; stay quiet.
      if (error && error.name === 'TypeError') {
        return;
      }
      console.error('Error loading connections:', error);
    }
  }

  function renderConnectionsList() {
    const treeView = document.querySelector('#connectorsTreeView');
    if (!treeView) return;

    // Clear existing content
    treeView.innerHTML = '';

    if (savedConnections.length === 0) {
      treeView.innerHTML = '<div style="padding: 10px; color: #999;">No connections yet</div>';
      return;
    }

    // Create list of connections
    const ul = document.createElement('ul');
    ul.className = 'mdl-list';
    ul.style.marginTop = '0';
    ul.style.paddingTop = '0';

    savedConnections.forEach(conn => {
      const li = document.createElement('li');
      li.className = 'mdl-list__item';
      li.style.minHeight = '40px';
      li.style.cursor = 'pointer';

      // Get database icon
      const dbType = conn.source_type;
      const config = DATABASE_CONFIG[dbType];

      li.innerHTML = `
                <span class="mdl-list__item-primary-content">
                    <i class="material-icons mdl-list__item-icon" style="font-size: 20px;">storage</i>
                    <span style="font-size: 14px;">${conn.name}</span>
                </span>
                <span class="mdl-list__item-secondary-content">
                    <span style="font-size: 12px; color: #999; margin-right: 10px;">${config?.name || dbType}</span>
                    <button class="mdl-button mdl-js-button mdl-button--icon edit-connection-btn" data-conn-id="${conn.id}" title="Edit connection">
                        <i class="material-icons">edit</i>
                    </button>
                    <button class="mdl-button mdl-js-button mdl-button--icon delete-connection-btn" data-conn-id="${conn.id}" title="Delete connection">
                        <i class="material-icons">delete</i>
                    </button>
                </span>
            `;

      // Add click handler to explore tables
      li.addEventListener('click', function (e) {
        if (
          !e.target.closest('.delete-connection-btn') &&
          !e.target.closest('.edit-connection-btn')
        ) {
          exploreConnection(conn.id);
        }
      });

      ul.appendChild(li);
    });

    treeView.appendChild(ul);

    // Add edit handlers
    document.querySelectorAll('.edit-connection-btn').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        editConnection(this.dataset.connId);
      });
    });

    // Add delete handlers
    document.querySelectorAll('.delete-connection-btn').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        deleteConnection(this.dataset.connId);
      });
    });

    // Upgrade MDL components
    if (typeof componentHandler !== 'undefined') {
      componentHandler.upgradeAllRegistered();
    }
  }

  async function exploreConnection(connectionId) {
    // This could open a dialog showing tables or redirect to a data exploration view
    console.log('Exploring connection:', connectionId);

    try {
      const secret = window.userData?.secret || localStorage.getItem('secret');
      const response = await fetch(
        `/api/list-tables?secret=${secret}&data_source_id=${connectionId}`
      );
      const result = await response.json();

      if (result.success && result.tables) {
        showTablesDialog(connectionId, result.tables);
      } else {
        showSnackbar('Failed to fetch tables: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      showSnackbar('Error: ' + error.message);
    }
  }

  function showTablesDialog(connectionId, tables) {
    // For now, just log the tables
    console.log('Tables for connection', connectionId, ':', tables);

    // You could create another dialog here to show the tables
    // and allow the user to query them
    showSnackbar(`Found ${tables.length} tables`);
  }

  function editConnection(connectionId) {
    // Find the connection in our saved connections
    const connection = savedConnections.find(conn => conn.id === connectionId);
    if (!connection) {
      showSnackbar('Connection not found');
      return;
    }

    // Reset dialog first
    resetDialog();

    // Open the connector dialog
    const dialog = document.querySelector('#data-connector-dialog');
    dialog.showModal();

    // Set the database type (this will show the form and update UI)
    selectDatabaseType(connection.source_type);

    // Populate the form with existing connection data
    document.querySelector('#conn-name').value = connection.name || '';

    if (connection.connection_string) {
      // Use connection string mode
      document.querySelector('#use-conn-string').checked = true;
      toggleConnectionStringMode(true);
      document.querySelector('#conn-string').value = connection.get_decrypted_connection_string
        ? connection.get_decrypted_connection_string()
        : connection.connection_string;
    } else {
      // Use individual fields mode
      document.querySelector('#use-conn-string').checked = false;
      toggleConnectionStringMode(false);
      document.querySelector('#conn-host').value = connection.host || '';
      document.querySelector('#conn-port').value = connection.port || '';
      document.querySelector('#conn-database').value = connection.database || '';
      document.querySelector('#conn-username').value = connection.username || '';
      document.querySelector('#conn-schema').value = connection.schema || '';
      // Note: We don't populate the password for security reasons
    }

    // Update the save button to handle editing
    const saveBtn = document.querySelector('#save-connection-btn');
    saveBtn.textContent = 'Update Connection';
    saveBtn.removeEventListener('click', saveConnection);
    saveBtn.addEventListener('click', function () {
      updateConnection(connectionId);
    });

    // Update MDL text fields to show the populated values
    if (typeof componentHandler !== 'undefined') {
      componentHandler.upgradeAllRegistered();
    }

    // Keep floating labels aligned with programmatic values.
    syncConnectionFormTextFields();
  }

  async function updateConnection(connectionId) {
    const saveBtn = document.querySelector('#save-connection-btn');

    // Disable button during update
    saveBtn.disabled = true;
    saveBtn.textContent = 'Updating...';

    try {
      const connectionData = getConnectionData();

      // Add connection ID for update
      connectionData.id = connectionId;
      connectionData.secret = window.userData?.secret || localStorage.getItem('secret');

      const response = await fetch('/api/data-sources', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(connectionData)
      });

      const result = await response.json();

      if (result.success) {
        // Close dialog
        const dialog = document.querySelector('#data-connector-dialog');
        closeDialog(dialog);

        // Show success message
        showSnackbar('Connection updated successfully');

        // Reload connections list
        loadConnections();
      } else {
        showSnackbar('Failed to update connection: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating connection:', error);
      showSnackbar('Error: ' + error.message);
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Update Connection';
    }
  }

  async function deleteConnection(connectionId) {
    if (!confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    try {
      const secret = window.userData?.secret || localStorage.getItem('secret');
      const response = await fetch('/api/data-sources', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          secret: secret,
          data_source_id: connectionId
        })
      });

      const result = await response.json();

      if (result.success) {
        showSnackbar('Connection deleted');
        loadConnections();
      } else {
        showSnackbar('Failed to delete connection: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting connection:', error);
      showSnackbar('Error: ' + error.message);
    }
  }

  async function indexDatabaseTables(dataSourceId) {
    // Index tables in the background after saving a connection
    try {
      const secret = window.userData?.secret || localStorage.getItem('secret');
      await fetch(`/api/list-tables?secret=${secret}&data_source_id=${dataSourceId}`);
      console.log('Tables indexed for data source:', dataSourceId);
    } catch (error) {
      console.error('Error indexing tables:', error);
    }
  }

  function showSnackbar(message) {
    const snackbar = document.querySelector('#share-snackbar');
    if (snackbar && snackbar.MaterialSnackbar) {
      snackbar.MaterialSnackbar.showSnackbar({
        message: message,
        timeout: 3000
      });
    } else {
      // Fallback to console log
      console.log(message);
    }
  }

  // Export for global access
  window.DataConnectors = {
    load: loadConnections,
    open: openConnectorDialog
  };
  window.openDataConnectorDialog = openConnectorDialog;
})();
